// src/utils/handles.ts
export type ParsedHandle = {
  raw: string;           // original input
  platform: 'youtube' | 'instagram' | 'tiktok' | 'twitter' | 'facebook' | 'linkedin' | 'newsletter' | 'unknown';
  handle: string;        // canonical handle (no leading @)
  normalized: string;    // lowercased or canonical for storage (keep dots/dashes/underscores)
};

/**
 * Parse a handle or URL to extract platform and canonical handle
 * 
 * Robust parser that handles:
 * - Full URLs (youtube.com/@handle, instagram.com/handle, etc.)
 * - Shortened URLs (t.co, bit.ly, etc.)
 * - Raw handles (@handle, handle)
 * - Various platform URL patterns
 * 
 * @param input URL string or handle to parse
 * @returns ParsedHandle object with platform, handle, and normalized values
 * 
 * @example
 * parseHandle('https://youtube.com/@channelname')
 * // { platform: 'youtube', handle: 'channelname', normalized: 'channelname', raw: '...' }
 * 
 * parseHandle('@instagram_handle')
 * // { platform: 'unknown', handle: 'instagram_handle', normalized: 'instagram_handle', raw: '...' }
 */
export function parseHandle(input: string): ParsedHandle {
  const raw = (input ?? '').trim();

  // helper
  const tryMatch = (re: RegExp) => {
    const m = raw.match(re);
    return m ? m[1] : null;
  };

  // YouTube: matches @handle or /@handle
  if (/youtube\.com|youtu\.be/i.test(raw)) {
    // prefer @handle - capture full username including dots, dashes, underscores, plus signs, etc.
    const at = tryMatch(/@([^/?#\s]+)/);
    if (at) return { raw, platform: 'youtube', handle: at, normalized: at };
    // fallback: user/playlist/channel patterns
    const byPath = tryMatch(/(?:youtube\.com\/(?:user|channel|c)\/([^/?#\s]+))/i) || tryMatch(/youtu\.be\/([^/?#\s]+)/i);
    if (byPath) return { raw, platform: 'youtube', handle: byPath, normalized: byPath };
  }

  // Instagram: matches @handle or /handle
  if (/instagram\.com/i.test(raw)) {
    // Capture full username including dots, dashes, underscores, plus signs, etc.
    const user = tryMatch(/instagram\.com\/@?([^/?#\s]+)/i) || tryMatch(/instagram\.com\/p\/([^/?#\s]+)/i);
    if (user) return { raw, platform: 'instagram', handle: user, normalized: user.toLowerCase() };
  }

  // TikTok: matches @handle
  if (/tiktok\.com/i.test(raw)) {
    // Capture full username including dots, dashes, underscores, plus signs, etc.
    const user = tryMatch(/tiktok\.com\/@([^/?#\s]+)/i) || tryMatch(/vm\.tiktok\.com\/([^/?#\s]+)/i);
    if (user) return { raw, platform: 'tiktok', handle: user, normalized: user };
  }

  // Twitter / X: matches @handle
  if (/twitter\.com|x\.com/i.test(raw)) {
    // Capture full username including dots, dashes, underscores, plus signs, etc.
    const user = tryMatch(/(?:twitter\.com|x\.com)\/@?([^/?#\s]+)/i);
    if (user) return { raw, platform: 'twitter', handle: user, normalized: user.toLowerCase() };
  }

  // Facebook: matches /username (no @ symbol)
  if (/facebook\.com/i.test(raw)) {
    // Capture full username including dots, dashes, underscores, plus signs, etc.
    // Handle various Facebook URL patterns: /username, /pg/username, /profile.username, etc.
    const user = tryMatch(/facebook\.com\/(?:pg\/)?([^/?#\s]+)/i) || 
                 tryMatch(/facebook\.com\/profile\.php\?id=([^&]+)/i);
    if (user) return { raw, platform: 'facebook', handle: user, normalized: user };
  }

  // LinkedIn: matches /in/username or /company/username (no @ symbol)
  if (/linkedin\.com/i.test(raw)) {
    // Capture full username including dots, dashes, underscores, plus signs, etc.
    const user = tryMatch(/linkedin\.com\/in\/([^/?#\s]+)/i) || 
                 tryMatch(/linkedin\.com\/company\/([^/?#\s]+)/i) ||
                 tryMatch(/linkedin\.com\/pub\/([^/?#\s]+)/i);
    if (user) return { raw, platform: 'linkedin', handle: user, normalized: user };
  }

  // Newsletter / raw link fallback
  if (/^(https?:\/\/)/i.test(raw)) {
    return { raw, platform: 'newsletter', handle: raw, normalized: raw };
  }

  // Raw @handle or plain handle
  if (raw.startsWith('@')) {
    const h = raw.slice(1);
    return { raw, platform: 'unknown', handle: h, normalized: h };
  }

  // Plain text fallback
  return { raw, platform: 'unknown', handle: raw, normalized: raw };
}

/**
 * Extract handle string from URL or input (backward compatibility)
 * 
 * @param input URL string or handle to parse
 * @returns Extracted handle string (without @ symbol)
 * 
 * @deprecated Use parseHandle() for full parsing capabilities
 */
export function extractHandle(input: string): string {
  return parseHandle(input).handle;
}

/**
 * Detailed handle extraction (backward compatibility alias)
 * 
 * @deprecated Use parseHandle() directly
 */
export const extractHandleDetailed = parseHandle;

export default parseHandle; 